import { Alert, Empty, Modal, Space } from "antd";
import { useMemo } from "react";
import { AnalysisBubble } from "./AnalysisBubble";
import { ConsumptionTable } from "./ConsumptionTable";
import { FindingsBadges } from "./FindingsBadges";
import type { AIAnalysis, AIHighlight } from "./types";

interface AIAnalysisModalProps {
  analysis: AIAnalysis | null;
  open: boolean;
  onClose: () => void;
}

export const AIAnalysisModal: React.FC<AIAnalysisModalProps> = ({ analysis, open, onClose }) => {
  const highlights: AIHighlight[] = useMemo(() => {
    if (!analysis?.highlights) return [];
    try {
      const parsed = JSON.parse(analysis.highlights);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [analysis?.highlights]);

  const consumptionData = useMemo(() => {
    if (!analysis?.input_data) return [];
    try {
      const parsed = JSON.parse(analysis.input_data);
      return Array.isArray(parsed.consumption_table) ? parsed.consumption_table : [];
    } catch {
      return [];
    }
  }, [analysis?.input_data]);

  if (!analysis) return null;

  const title = `ИИ-анализ${analysis.object_name ? ` — ${analysis.object_name}` : ""}`;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(90vw, 800px)"
      destroyOnClose
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      {analysis.status === "error" ? (
        <Alert
          type="error"
          message="Ошибка анализа"
          description={analysis.error_message ?? "Неизвестная ошибка"}
          showIcon
        />
      ) : analysis.status === "empty" ? (
        <Empty description="Нет данных для анализа по объекту" />
      ) : analysis.status === "pending" ? (
        <Alert type="info" message="Анализ выполняется..." showIcon />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {highlights.length > 0 && <FindingsBadges highlights={highlights} />}
          {analysis.summary && <AnalysisBubble content={analysis.summary} />}
          {consumptionData.length > 0 && <ConsumptionTable data={consumptionData} />}
          {analysis.status === "partial" && (
            <Alert
              type="info"
              message="Частичный анализ"
              description="ИИ-сервис был недоступен. Показаны только автоматические находки."
              showIcon
            />
          )}
        </Space>
      )}
    </Modal>
  );
};
